import { chapterWordCount, normalizeChapters, type ChapterRecord } from "./safe-data";
import { CLEAN_FILENAMES_ON, dedupeTags, stripLeadingNumberPrefix } from "./litera-clean-filenames";

export type ManuscriptFileRecord = {
  name?: string;
  textContent?: string;
  id?: string;
};

/** Normalize uploaded manuscript text without splitting on chapter headings. */
export const wholeDocumentBody = (text: unknown): string =>
  String(text || "")
    .replace(/\r\n/g, "\n")
    .replace(/\[BBG_MANUSCRIPT_(?:START|END)\]/gi, "")
    .replace(/(?:^|\n)##?\s*(?:Final Manuscript|Færdigt manuskript|Manuskript)\s*\n/gi, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

export const chapterIdForNumber = (chapter: { id?: number } | null | undefined): string =>
  `chapter-${chapter?.id ?? 1}`;

const manuscriptTitleFromFile = (name: unknown): string => {
  const base = String(name || "").replace(/\.[^.]+$/i, "");
  // Clean-filenames fix: strip the leading "NN_" chapter prefix so the title
  // doesn't carry it back into the next filename (the sync→save growth loop).
  const cleaned = CLEAN_FILENAMES_ON ? stripLeadingNumberPrefix(base) : base;
  return cleaned.trim() || "Manuskript";
};

/** Library tags after syncing a whole manuscript into the editor (no auto-split). */
export const manuscriptEditorTags = (existingTags: string[] = []): string[] => {
  const tags = [
    ...existingTags.filter(
      tag => tag !== "synced-to-chapter" && tag !== "split-into-chapters" && tag !== "whole-book",
    ),
    "synced-to-editor",
    "whole-book",
  ];
  return CLEAN_FILENAMES_ON ? dedupeTags(tags) : tags;
};

export type WholeManuscriptSyncResult = {
  chapters: ChapterRecord[];
  chapterId: string | null;
  chapterNumber: number;
  chapterCount: number;
  wordCount: number;
};

export interface MergeSyncFile {
  id?: string;
  name?: string;
  textContent?: string;
}

/** Leading NN_ / NN- chapter-number prefix from a manuscript filename (for ordering). */
function chapterOrderPrefix(name: unknown): number {
  const m = /^(\d+)\s*[_-]/.exec(String(name || "").trim());
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER; // unprefixed → sort last
}

/**
 * Merge-sync (VITE_FF_MERGE_SYNC): build the editor working set from N selected manuscript
 * files — ONE chapter per file, ordered by the numeric NN_ filename prefix (= chapter id;
 * sorts to creation/chapter order for both sequential and monotonic-timestamp ids alike).
 * Empty files are dropped. Returns a fresh chapters array to set ONCE (a single replace with
 * the FULL set — not N per-file replaces). Pure (no React/IO) → testable.
 */
export function buildEditorChaptersFromFiles(
  files: MergeSyncFile[] | null | undefined,
): ChapterRecord[] {
  const list = Array.isArray(files) ? files : [];
  const withText = list
    .map((f) => ({ f, body: wholeDocumentBody(f?.textContent) }))
    .filter((x) => x.body.length > 0)
    .sort((a, b) => {
      const pa = chapterOrderPrefix(a.f.name);
      const pb = chapterOrderPrefix(b.f.name);
      if (pa !== pb) return pa - pb;
      return String(a.f.name || "").localeCompare(String(b.f.name || ""), undefined, { numeric: true });
    });
  const chapters = withText.map((x, i) => ({
    id: i + 1,
    title: manuscriptTitleFromFile(x.f.name),
    content: x.body,
    status: "DRAFT" as const,
    sourceDocumentId: x.f.id,
    sourceDocumentName: x.f.name,
  }));
  return normalizeChapters(chapters);
}

/** Import/upload: one editor chapter with the full manuscript text. */
export function syncWholeManuscriptToEditor(
  chapters: unknown,
  fileRecord: ManuscriptFileRecord,
  action: "replace" | "append" = "replace",
): WholeManuscriptSyncResult {
  const text = wholeDocumentBody(fileRecord.textContent);
  const base = normalizeChapters(chapters);
  const title = manuscriptTitleFromFile(fileRecord.name);

  if (!text) {
    return {
      chapters: base,
      chapterId: base.length ? chapterIdForNumber(base[0]) : null,
      chapterNumber: base.length ? 1 : 0,
      chapterCount: base.length ? 1 : 0,
      wordCount: 0,
    };
  }

  if (action === "append" && base.length) {
    const index = base.length - 1;
    const current = base[index];
    const content = String(current.content || "").trim()
      ? `${String(current.content).trim()}\n\n${text}`
      : text;
    const next = [...base];
    next[index] = {
      ...current,
      content,
      sourceDocumentId: fileRecord.id,
      sourceDocumentName: fileRecord.name,
    };
    const normalized = normalizeChapters(next);
    return {
      chapters: normalized,
      chapterId: chapterIdForNumber(normalized[index]),
      chapterNumber: index + 1,
      chapterCount: 1,
      wordCount: chapterWordCount(content),
    };
  }

  const normalized = normalizeChapters([{
    id: 1,
    title,
    content: text,
    status: "DRAFT",
    sourceDocumentId: fileRecord.id,
    sourceDocumentName: fileRecord.name,
  }]);
  return {
    chapters: normalized,
    chapterId: chapterIdForNumber(normalized[0]),
    chapterNumber: 1,
    chapterCount: 1,
    wordCount: chapterWordCount(text),
  };
}
